import React from 'react';
import { Portal } from 'react-portal';

export default ({ isOpen, image, onSaved, onExited, onOriginalImageRequested }) => {
  const imageObject = React.useRef(null);
  const canvas = React.useRef();
  const context = React.useRef();
  const [canvasDimensions, setCanvasDimensions] = React.useState({
    width: 800,
    height: 1000
  });
  const boxes = React.useRef([]);
  const [isExiting, setIsExiting] = React.useState(false);
  const [canUndo, setCanUndo] = React.useState(false)
  const [scaleDelta, setScaleDelta] = React.useState(1);

  const saveAndClose = () => {
    onSaved(canvas.current.toDataURL('image/png', 1.0))
    setIsExiting(true);
  };

  const draw = () => {
    const ctx = context.current;
    if (ctx && imageObject.current) {
      ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);
      ctx.drawImage(imageObject.current, 0, 0);
      boxes.current.forEach(box => {
        ctx.fillStyle = 'rgb(0,0,0)';
        ctx.fillRect(box.x1, box.y1, box.x2 - box.x1, box.y2 - box.y1);
      });
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      context.current = canvas.current.getContext('2d');
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (image && isOpen) {
      const img = new Image();
      img.src = image;
      img.addEventListener("load", () => {
        imageObject.current = img;
        setCanvasDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
        draw();
      })
    }
  }, [image, isOpen]);

  const handleDrag = e => {
    const { left, top } = canvas.current.getBoundingClientRect();
    const lastBox = boxes.current[boxes.current.length - 1];
    boxes.current = [
      ...boxes.current.slice(0, boxes.current.length - 1),
      {
        ...lastBox,
        x2: (e.clientX - left) * scaleDelta,
        y2: (e.clientY - top) * scaleDelta
      }
    ];
    draw();
  };

  const handleMouseDown = e => {
    document.addEventListener('mousemove', handleDrag);
    const { left, top } = canvas.current.getBoundingClientRect();
    boxes.current.push({
      x1: (e.clientX - left) * scaleDelta,
      y1: (e.clientY - top) * scaleDelta,
      x2: (e.clientX - left) * scaleDelta,
      y2: (e.clientY - top) * scaleDelta
    });
  };

  const handleMouseUp = e => {
    document.removeEventListener('mousemove', handleDrag);
    setCanUndo(true)
  };

  const clearBoxes = () => {
    boxes.current = [];
    onOriginalImageRequested()
    draw();
    setCanUndo(false)
  };

  const undoBox = () => {
    if(boxes.current.length){
      boxes.current.pop();
      draw();
      setCanUndo(!!boxes.current.length)
    }
  };

  const handleAnimationEnd = () => {
    if (isExiting) {
      onExited();
      setIsExiting(false);
      boxes.current = []
      setCanUndo(false)
    }
  };

  const calculateScale = React.useCallback(() => {
    if(canvas.current){
      const currentRect = canvas.current.getBoundingClientRect()
      setScaleDelta(canvasDimensions.width / currentRect.width)
    }
  }, [canvasDimensions.width])

  React.useEffect(() => {
    calculateScale()
    window.addEventListener("resize", calculateScale)
    return () => {
      window.removeEventListener('resize', calculateScale)
    }
  }, [calculateScale])

  return isOpen ? (
    <Portal>
      <div
        className={`image-redaction-wrapper ${isExiting ? 'slide-down' : ''}`}
        onAnimationEnd={handleAnimationEnd}
      >
        <canvas
          width={canvasDimensions.width}
          height={canvasDimensions.height}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          ref={canvas}
        ></canvas>
      </div>
      <div className={`shade ${isExiting ? 'fade-out' : ''}`} />
      <div
        className={`image-redaction-toolbar ${
          isExiting ? 'slide-down-big' : ''
        }`}
      >
        <div className="inner-wrapper">
          <button
            className="redaction-toolbar-button neutral left"
            onClick={clearBoxes}
          >
            Clear
          </button>
          <button
            className="redaction-toolbar-button neutral right"
            onClick={undoBox}
            disabled={!canUndo}
          >
            Undo
          </button>
          <button
            className="redaction-toolbar-button success"
            style={{ marginLeft: 'auto' }}
            onClick={saveAndClose}
          >
            Done
          </button>
        </div>
      </div>
    </Portal>
  ) : null;
};
